import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Stack,
  InputAdornment,
  CircularProgress,
  Alert,
  Container,
  Fade,
  Grow,
  Paper,
  Badge,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Campaign as CampaignIcon,
  Handshake as PartnershipIcon,
  Launch as LaunchIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import client from '../../../API';
import routes from '../../../Config/routes';
import profilePhoto from '../../../Components/globalAssets/ppfLogo.png';
import { handleAvatarError } from '../../../Utils';

const statusColorMap = {
  Pitched: 'warning',
  Accepted: 'info',
  Completed: 'success',
  Launched: 'success',
  pending: 'warning',
  active: 'success',
  completed: 'success',
};

const ModernCard = ({ children, onClick, ...props }) => {
  const theme = useTheme();
  
  return (
    <Card
      {...props}
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
          '& .card-media': {
            transform: 'scale(1.1)',
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        }
      }}
      onClick={onClick}
    >
      {children}
    </Card>
  );
};

const GlassmorphicPaper = ({ children, ...props }) => {
  const theme = useTheme();
  
  return (
    <Paper
      {...props}
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2,
        ...props.sx
      }}
    >
      {children}
    </Paper>
  );
};

export default function AgencyAllCollabs() {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading, error } = useQuery(
    'agencyAllCollaborations',
    client.agency.getAllCollaborations,
    {
      refetchOnWindowFocus: false,
    }
  );

  const collaborations = data?.collaborations || [];

  const filteredCollaborations = useMemo(() => {
    return collaborations.filter((item) => {
      // Type filter
      const typeMatch = 
        tabValue === 'all' ? true :
        tabValue === 'campaigns' ? item.type === 'campaign' :
        tabValue === 'partnerships' ? item.type === 'partnership' :
        true;

      // Status filter
      const statusMatch = 
        statusFilter === 'all' ? true :
        statusFilter === 'active' ? 
          (item.type === 'campaign' ? item.campaign_status === 'Launched' : item.status === 'active') :
        statusFilter === 'completed' ?
          (item.type === 'campaign' ? item.status === 'Completed' || item.campaign_status === 'Completed' : item.status === 'completed') :
        statusFilter === 'pending' ?
          (item.type === 'campaign' ? item.status === 'Pitched' : item.status === 'pending') :
        true;

      // Search filter
      const searchMatch = 
        searchQuery === '' ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.creator_display_name.toLowerCase().includes(searchQuery.toLowerCase());

      return typeMatch && statusMatch && searchMatch;
    });
  }, [collaborations, tabValue, statusFilter, searchQuery]);

  const handleItemClick = (item) => {
    const creatorId = item.creator_id;
    if (item.type === 'campaign') {
      navigate(`/agency/creators/${creatorId}/collaborations/campaigns/${item.id}`);
    } else if (item.type === 'partnership') {
      navigate(`/agency/creators/${creatorId}/collaborations/partnerships/${item.id}`);
    }
  };

  const getStatusChip = (item) => {
    const status = item.type === 'campaign' ? item.status : item.status;
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A';
    const color = statusColorMap[status] || 'default';
    
    return (
      <Chip 
        label={label} 
        color={color} 
        size="small" 
        sx={{
          fontWeight: 600,
          borderRadius: 2,
          '& .MuiChip-label': {
            px: 1.5
          }
        }}
      />
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load collaborations. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <GlassmorphicPaper 
            elevation={0} 
            sx={{ 
              mb: 3,
              p: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary
                }}
              >
                All Collaborations
                <Chip
                  label={filteredCollaborations.length}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, fontWeight: 600 }}
                />
              </Typography>
            </Box>

            {/* Search and Filters */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search by name or creator..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ 
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    background: alpha(theme.palette.background.paper, 0.8),
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Stack>

            {/* Status Filter Chips */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label="All"
                onClick={() => setStatusFilter('all')}
                color={statusFilter === 'all' ? 'primary' : 'default'}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label="Active"
                onClick={() => setStatusFilter('active')}
                color={statusFilter === 'active' ? 'success' : 'default'}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label="Pending"
                onClick={() => setStatusFilter('pending')}
                color={statusFilter === 'pending' ? 'warning' : 'default'}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label="Completed"
                onClick={() => setStatusFilter('completed')}
                color={statusFilter === 'completed' ? 'info' : 'default'}
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </GlassmorphicPaper>

          {/* Tabs */}
          <GlassmorphicPaper elevation={0} sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{
                '& .MuiTabs-indicator': { 
                  height: 4,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                },
              }}
            >
              <Tab label="All" value="all" />
              <Tab label="Campaigns" value="campaigns" />
              <Tab label="Partnerships" value="partnerships" />
            </Tabs>
          </GlassmorphicPaper>

          {/* Grid */}
          <Fade in timeout={1000}>
            <Grid container spacing={3}>
              {filteredCollaborations.length === 0 ? (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary">
                      No collaborations found
                    </Typography>
                  </Box>
                </Grid>
              ) : (
                filteredCollaborations.map((item, index) => (
                  <Grow
                    in
                    timeout={800 + index * 100}
                    key={`${item.type}-${item.id}-${item.creator_id}`}
                  >
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <ModernCard onClick={() => handleItemClick(item)}>
                        {/* Cover Image */}
                        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                          <CardMedia
                            component="img"
                            height="220"
                            image={item.photo || item.pfphref || profilePhoto}
                            alt={item.name}
                            className="card-media"
                            sx={{
                              transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                              objectFit: 'cover'
                            }}
                          />
                          
                          {/* Creator Avatar Badge */}
                          
                        </Box>

                        {/* Card Body */}
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                            <Chip
                              label={item.type}
                              size="small"
                              color={item.type === 'campaign' ? 'secondary' : 'primary'}
                              sx={{
                                textTransform: 'capitalize',
                                fontWeight: 600,
                                borderRadius: 2
                              }}
                            />
                            {getStatusChip(item)}
                          </Box>

                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              mb: 2,
                              fontSize: '1.1rem',
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {item.name}
                          </Typography>

                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                              <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                              <Typography variant="body2" noWrap>
                                {item.creator_display_name}
                              </Typography>
                            </Box>

                            {item.rate > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                <AttachMoneyIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                                  ${item.rate.toFixed(2)}
                                </Typography>
                              </Box>
                            )}

                            {item.due_date && (
                              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                <ScheduleIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                                <Typography variant="caption">
                                  Due: {new Date(item.due_date).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </CardContent>

                        {/* Card Actions */}
                        <CardActions
                          sx={{
                            justifyContent: 'flex-end',
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            px: 2,
                            py: 1.5,
                            background: alpha(theme.palette.background.default, 0.3)
                          }}
                        >
                          <Button
                            size="small"
                            endIcon={<LaunchIcon />}
                            sx={{ fontWeight: 600 }}
                          >
                            View Details
                          </Button>
                        </CardActions>
                      </ModernCard>
                    </Grid>
                  </Grow>
                ))
              )}
            </Grid>
          </Fade>
        </Box>
      </Fade>
    </Container>
  );
}